export interface MonthlyData {
  month: string;
  label: string;
  income: number;
  expenses: number;
  fixed: number;
  variable: number;
}

export interface ForecastPoint {
  month: string;
  label: string;
  predicted_expenses: number;
  predicted_income: number;
  predicted_fixed: number;
  predicted_variable: number;
  confidence: number;
  trend: "up" | "down" | "stable";
}

export interface LegacyInsight {
  type: "warning" | "info" | "success";
  title: string;
  message: string;
}

export interface ForecastInsight {
  type: "risk" | "opportunity";
  severity: 1 | 2 | 3 | 4 | 5;
  message: string;
}

export interface DecompositionResult {
  trend: number[];
  seasonality: number[];
  residual: number[];
  seasonalIndex: number[];
}

export interface ForecastStatistics {
  avg_monthly_expenses: number;
  avg_monthly_income: number;
  expense_trend: number;
  income_trend: number;
  volatility: number;
  confidence_score: number;
  runway_months: number | null;
}

export interface ForecastScenarios {
  base: ForecastPoint[];
  optimistic: ForecastPoint[];
  pessimistic: ForecastPoint[];
}

export interface RiskSimulationResult {
  iterations: number;
  liquidity_crisis_probability: number;
  confidence_interval_80: {
    p10: number;
    p90: number;
  };
  ending_cash_expected: number;
}

export interface DebtInstallment {
  month: string;
  amount: number;
}

export interface ForecastDrivers {
  active_clients_forecast?: number[];
  avg_ticket_forecast?: number[];
  volume_forecast?: number[];
  avg_price_forecast?: number[];
}

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends unknown[]
    ? T[K]
    : T[K] extends object
      ? DeepPartial<T[K]>
      : T[K];
};

export interface ForecastEngineConfig {
  holtWinters: {
    alpha: number;
    beta: number;
    gamma: number;
    seasonalPeriod: number;
  };
  driver: {
    enabled: boolean;
    overrideStatisticalWhenAvailable: boolean;
    statisticalWeight: number;
    driverWeight: number;
  };
  scenarios: {
    optimistic: {
      growthAdjustment: number;
      costAdjustment: number;
      volatilityMultiplier: number;
    };
    stress: {
      growthAdjustment: number;
      costAdjustment: number;
      volatilityMultiplier: number;
    };
  };
  constraints: {
    maxAnnualGrowthCap: number;
    maxMonthlyCapacityRevenue: number | null;
    openingCash: number;
    debtSchedule: DebtInstallment[];
  };
  monteCarlo: {
    enabled: boolean;
    iterations: number;
    seed: number;
    growthStdDev: number;
    costStdDev: number;
    collectionStdDev: number;
  };
  useLegacyConfidenceFallback: boolean;
}

export interface ForecastEngineInput {
  historical: MonthlyData[];
  monthsToPredict: number;
  drivers?: ForecastDrivers;
  config?: DeepPartial<ForecastEngineConfig>;
}

export interface ForecastEngineOutput {
  historical: MonthlyData[];
  forecasts: ForecastPoint[];
  forecast_scenarios: ForecastScenarios;
  insights: LegacyInsight[];
  advanced_insights: ForecastInsight[];
  warnings: string[];
  statistics: ForecastStatistics;
  risk_simulation: RiskSimulationResult | null;
}

interface StatisticalForecastResult {
  points: ForecastPoint[];
  avgExpenses: number;
  avgIncome: number;
  avgFixed: number;
  avgVariable: number;
  expenseTrendPct: number;
  incomeTrendPct: number;
  volatility: number;
  decomposition: {
    income: DecompositionResult;
    expenses: DecompositionResult;
  };
}

interface ConstraintApplicationResult {
  constrained: ForecastPoint[];
  warnings: string[];
  endingCash: number;
  averageMonthlyBurn: number;
}

const DEFAULT_CONFIG: ForecastEngineConfig = {
  holtWinters: {
    alpha: 0.35,
    beta: 0.2,
    gamma: 0.25,
    seasonalPeriod: 12,
  },
  driver: {
    enabled: true,
    overrideStatisticalWhenAvailable: false,
    statisticalWeight: 0.7,
    driverWeight: 0.3,
  },
  scenarios: {
    optimistic: {
      growthAdjustment: 0.08,
      costAdjustment: 0.05,
      volatilityMultiplier: 0.9,
    },
    stress: {
      growthAdjustment: 0.1,
      costAdjustment: 0.08,
      volatilityMultiplier: 1.2,
    },
  },
  constraints: {
    maxAnnualGrowthCap: 0.3,
    maxMonthlyCapacityRevenue: null,
    openingCash: 0,
    debtSchedule: [],
  },
  monteCarlo: {
    enabled: true,
    iterations: 200,
    seed: 42,
    growthStdDev: 0.05,
    costStdDev: 0.05,
    collectionStdDev: 0.03,
  },
  useLegacyConfidenceFallback: true,
};

export function getDefaultForecastEngineConfig(
  overrides?: DeepPartial<ForecastEngineConfig>,
): ForecastEngineConfig {
  return {
    ...DEFAULT_CONFIG,
    ...overrides,
    holtWinters: {
      ...DEFAULT_CONFIG.holtWinters,
      ...overrides?.holtWinters,
    },
    driver: {
      ...DEFAULT_CONFIG.driver,
      ...overrides?.driver,
    },
    scenarios: {
      optimistic: {
        ...DEFAULT_CONFIG.scenarios.optimistic,
        ...overrides?.scenarios?.optimistic,
      },
      stress: {
        ...DEFAULT_CONFIG.scenarios.stress,
        ...overrides?.scenarios?.stress,
      },
    },
    constraints: {
      ...DEFAULT_CONFIG.constraints,
      ...overrides?.constraints,
      debtSchedule:
        overrides?.constraints?.debtSchedule ?? DEFAULT_CONFIG.constraints.debtSchedule,
    },
    monteCarlo: {
      ...DEFAULT_CONFIG.monteCarlo,
      ...overrides?.monteCarlo,
    },
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function safeDiv(numerator: number, denominator: number): number {
  if (Math.abs(denominator) < 1e-9) return 0;
  return numerator / denominator;
}

function addMonths(monthKey: string, offset: number): string {
  const [year, month] = monthKey.split("-").map(Number);
  const baseDate = new Date(year, month - 1, 1);
  baseDate.setMonth(baseDate.getMonth() + offset);
  return `${baseDate.getFullYear()}-${String(baseDate.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  const names = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  return `${names[month - 1]} ${year}`;
}

function movingAverageTrend(values: number[], windowSize: number): number[] {
  if (values.length === 0) return [];
  const size = Math.max(3, windowSize % 2 === 0 ? windowSize + 1 : windowSize);
  const half = Math.floor(size / 2);
  const trend: number[] = [];

  for (let i = 0; i < values.length; i += 1) {
    const start = Math.max(0, i - half);
    const end = Math.min(values.length - 1, i + half);
    const segment = values.slice(start, end + 1);
    trend.push(segment.reduce((acc, cur) => acc + cur, 0) / segment.length);
  }

  return trend;
}

function linearRegressionSlope(values: number[]): number {
  const n = values.length;
  if (n < 2) return 0;

  const sumX = (n * (n - 1)) / 2;
  const sumY = values.reduce((acc, value) => acc + value, 0);
  const sumXY = values.reduce((acc, value, i) => acc + i * value, 0);
  const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;

  const denominator = n * sumX2 - sumX * sumX;
  if (Math.abs(denominator) < 1e-9) return 0;
  return (n * sumXY - sumX * sumY) / denominator;
}

function weightedAverage(values: number[]): number {
  if (values.length === 0) return 0;
  const weights = values.map((_, idx) => idx + 1);
  const weighted = values.reduce((acc, value, idx) => acc + value * weights[idx], 0);
  const totalWeight = weights.reduce((acc, value) => acc + value, 0);
  return safeDiv(weighted, totalWeight);
}

function stdDev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((acc, value) => acc + value, 0) / values.length;
  const variance =
    values.reduce((acc, value) => acc + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

export function decomposeTimeSeries(
  values: number[],
  seasonalPeriod = 12,
): DecompositionResult {
  if (values.length === 0) {
    return { trend: [], seasonality: [], residual: [], seasonalIndex: [] };
  }

  const trend = movingAverageTrend(values, Math.min(7, values.length));

  const seasonality = new Array(values.length).fill(0);
  const seasonalIndex = new Array(seasonalPeriod).fill(0);

  if (values.length >= seasonalPeriod) {
    const buckets = new Array(seasonalPeriod).fill(null).map(() => [] as number[]);
    for (let i = 0; i < values.length; i += 1) {
      const detrended = values[i] - trend[i];
      buckets[i % seasonalPeriod].push(detrended);
    }

    const rawSeasonal = buckets.map((bucket) =>
      bucket.length > 0
        ? bucket.reduce((acc, cur) => acc + cur, 0) / bucket.length
        : 0,
    );
    const seasonalMean = rawSeasonal.reduce((acc, cur) => acc + cur, 0) / rawSeasonal.length;
    for (let i = 0; i < seasonalPeriod; i += 1) {
      seasonalIndex[i] = rawSeasonal[i] - seasonalMean;
    }

    for (let i = 0; i < values.length; i += 1) {
      seasonality[i] = seasonalIndex[i % seasonalPeriod];
    }
  }

  const residual = values.map((value, idx) => value - trend[idx] - seasonality[idx]);
  return { trend, seasonality, residual, seasonalIndex };
}

function holtLinearForecast(
  values: number[],
  horizon: number,
  alpha: number,
  beta: number,
): number[] {
  if (values.length === 0) return new Array(horizon).fill(0);
  if (values.length === 1) return new Array(horizon).fill(Math.max(0, values[0]));

  let level = values[0];
  let trend = values[1] - values[0];

  for (let t = 1; t < values.length; t += 1) {
    const previousLevel = level;
    level = alpha * values[t] + (1 - alpha) * (level + trend);
    trend = beta * (level - previousLevel) + (1 - beta) * trend;
  }

  const forecast: number[] = [];
  for (let h = 1; h <= horizon; h += 1) {
    forecast.push(Math.max(0, level + h * trend));
  }
  return forecast;
}

function holtWintersAdditiveForecast(
  values: number[],
  horizon: number,
  seasonalPeriod: number,
  alpha: number,
  beta: number,
  gamma: number,
): number[] {
  if (values.length < seasonalPeriod) {
    return holtLinearForecast(values, horizon, alpha, beta);
  }

  const decomposition = decomposeTimeSeries(values, seasonalPeriod);
  const seasonal = [...decomposition.seasonalIndex];
  while (seasonal.length < seasonalPeriod) seasonal.push(0);

  let level = values[0] - seasonal[0];
  let trend = linearRegressionSlope(values);

  for (let t = 0; t < values.length; t += 1) {
    const seasonIdx = t % seasonalPeriod;
    const prevLevel = level;
    const seasonValue = seasonal[seasonIdx] ?? 0;

    level = alpha * (values[t] - seasonValue) + (1 - alpha) * (level + trend);
    trend = beta * (level - prevLevel) + (1 - beta) * trend;
    seasonal[seasonIdx] = gamma * (values[t] - level) + (1 - gamma) * seasonValue;
  }

  const forecast: number[] = [];
  for (let h = 1; h <= horizon; h += 1) {
    const seasonIdx = (values.length + h - 1) % seasonalPeriod;
    const prediction = level + h * trend + (seasonal[seasonIdx] ?? 0);
    forecast.push(Math.max(0, prediction));
  }
  return forecast;
}

export function applySeasonality(
  forecastValues: number[],
  seasonalIndex: number[],
  startIndex: number,
  weight = 0.3,
): number[] {
  if (seasonalIndex.length === 0) {
    return [...forecastValues];
  }

  return forecastValues.map((value, idx) => {
    const seasonal = seasonalIndex[(startIndex + idx) % seasonalIndex.length] ?? 0;
    return Math.max(0, value + seasonal * weight);
  });
}

function forecastSeries(
  values: number[],
  horizon: number,
  config: ForecastEngineConfig["holtWinters"],
): number[] {
  if (values.length < 6) {
    const baseline = weightedAverage(values);
    const slope = linearRegressionSlope(values);
    return new Array(horizon)
      .fill(0)
      .map((_, idx) => Math.max(0, baseline + slope * (idx + 1)));
  }

  if (values.length < config.seasonalPeriod) {
    return holtLinearForecast(values, horizon, config.alpha, config.beta);
  }

  return holtWintersAdditiveForecast(
    values,
    horizon,
    config.seasonalPeriod,
    config.alpha,
    config.beta,
    config.gamma,
  );
}

export function computeStatisticalForecast(
  historical: MonthlyData[],
  monthsToPredict: number,
  config: ForecastEngineConfig,
): StatisticalForecastResult {
  const sorted = [...historical].sort((a, b) => a.month.localeCompare(b.month));
  const expenses = sorted.map((item) => item.expenses);
  const incomes = sorted.map((item) => item.income);
  const fixedCosts = sorted.map((item) => item.fixed);
  const variableCosts = sorted.map((item) => item.variable);

  const avgExpenses = expenses.length > 0 ? expenses.reduce((a, b) => a + b, 0) / expenses.length : 0;
  const avgIncome = incomes.length > 0 ? incomes.reduce((a, b) => a + b, 0) / incomes.length : 0;
  const avgFixed = fixedCosts.length > 0 ? fixedCosts.reduce((a, b) => a + b, 0) / fixedCosts.length : 0;
  const avgVariable = variableCosts.length > 0 ? variableCosts.reduce((a, b) => a + b, 0) / variableCosts.length : 0;

  const expenseTrend = linearRegressionSlope(expenses);
  const incomeTrend = linearRegressionSlope(incomes);
  const expenseTrendPct = avgExpenses > 0 ? (expenseTrend / avgExpenses) * 100 : 0;
  const incomeTrendPct = avgIncome > 0 ? (incomeTrend / avgIncome) * 100 : 0;
  const volatility = avgExpenses > 0 ? (stdDev(expenses) / avgExpenses) * 100 : 0;

  const expenseDecomposition = decomposeTimeSeries(expenses, config.holtWinters.seasonalPeriod);
  const incomeDecomposition = decomposeTimeSeries(incomes, config.holtWinters.seasonalPeriod);

  const predictedExpensesBase = forecastSeries(expenses, monthsToPredict, config.holtWinters);
  const predictedIncomeBase = forecastSeries(incomes, monthsToPredict, config.holtWinters);
  const predictedFixedBase = forecastSeries(fixedCosts, monthsToPredict, config.holtWinters);
  const predictedVariableBase = forecastSeries(variableCosts, monthsToPredict, config.holtWinters);

  const useSeasonality = sorted.length >= config.holtWinters.seasonalPeriod && sorted.length < 24;
  const predictedExpenses = useSeasonality
    ? applySeasonality(
        predictedExpensesBase,
        expenseDecomposition.seasonalIndex,
        sorted.length % config.holtWinters.seasonalPeriod,
        0.3,
      )
    : predictedExpensesBase;

  const predictedIncome = useSeasonality
    ? applySeasonality(
        predictedIncomeBase,
        incomeDecomposition.seasonalIndex,
        sorted.length % config.holtWinters.seasonalPeriod,
        0.2,
      )
    : predictedIncomeBase;

  const lastMonth = sorted[sorted.length - 1]?.month ?? new Date().toISOString().slice(0, 7);
  const points: ForecastPoint[] = [];

  for (let i = 0; i < monthsToPredict; i += 1) {
    const month = addMonths(lastMonth, i + 1);
    const fixed = Math.max(0, predictedFixedBase[i] ?? 0);
    const variable = Math.max(0, predictedVariableBase[i] ?? 0);
    const expensesValue = Math.max(0, Math.max(predictedExpenses[i] ?? 0, fixed + variable));
    const incomeValue = Math.max(0, predictedIncome[i] ?? 0);
    const expenseChangePct = avgExpenses > 0 ? ((expensesValue - avgExpenses) / avgExpenses) * 100 : 0;
    const trend: "up" | "down" | "stable" =
      expenseChangePct > 5 ? "up" : expenseChangePct < -5 ? "down" : "stable";

    points.push({
      month,
      label: monthLabel(month),
      predicted_expenses: round2(expensesValue),
      predicted_income: round2(incomeValue),
      predicted_fixed: round2(fixed),
      predicted_variable: round2(variable),
      confidence: 0,
      trend,
    });
  }

  return {
    points,
    avgExpenses,
    avgIncome,
    avgFixed,
    avgVariable,
    expenseTrendPct,
    incomeTrendPct,
    volatility,
    decomposition: {
      income: incomeDecomposition,
      expenses: expenseDecomposition,
    },
  };
}

function computeVariableCostRatio(historical: MonthlyData[]): number {
  const incomeSum = historical.reduce((acc, item) => acc + Math.max(0, item.income), 0);
  const variableSum = historical.reduce((acc, item) => acc + Math.max(0, item.variable), 0);
  if (incomeSum <= 0) return 0.25;
  return clamp(variableSum / incomeSum, 0.02, 0.9);
}

function driverRevenueAt(
  drivers: ForecastDrivers,
  idx: number,
): number | null {
  const clients = drivers.active_clients_forecast?.[idx];
  const ticket = drivers.avg_ticket_forecast?.[idx];
  if (typeof clients === "number" && typeof ticket === "number") {
    return clients * ticket;
  }

  const volume = drivers.volume_forecast?.[idx];
  const avgPrice = drivers.avg_price_forecast?.[idx];
  if (typeof volume === "number" && typeof avgPrice === "number") {
    return volume * avgPrice;
  }

  return null;
}

export function computeDriverForecast(
  baseForecast: ForecastPoint[],
  historical: MonthlyData[],
  drivers: ForecastDrivers | undefined,
  config: ForecastEngineConfig,
): ForecastPoint[] {
  if (!drivers || !config.driver.enabled) {
    return [...baseForecast];
  }

  const variableRatio = computeVariableCostRatio(historical);
  const statsWeight = clamp(config.driver.statisticalWeight, 0, 1);
  const driverWeight = clamp(config.driver.driverWeight, 0, 1);
  const normalizedWeight = statsWeight + driverWeight > 0 ? statsWeight + driverWeight : 1;

  return baseForecast.map((point, idx) => {
    const revenueFromDrivers = driverRevenueAt(drivers, idx);
    if (revenueFromDrivers === null) {
      return point;
    }

    const blendedIncome = config.driver.overrideStatisticalWhenAvailable
      ? revenueFromDrivers
      : ((point.predicted_income * statsWeight) + (revenueFromDrivers * driverWeight)) / normalizedWeight;

    const predictedVariable = Math.max(0, blendedIncome * variableRatio);
    const predictedFixed = Math.max(0, point.predicted_fixed);
    const predictedExpenses = Math.max(0, predictedFixed + predictedVariable);
    const expenseTrendBasis = safeDiv(predictedExpenses - point.predicted_expenses, point.predicted_expenses || 1) * 100;
    const trend: "up" | "down" | "stable" =
      expenseTrendBasis > 5 ? "up" : expenseTrendBasis < -5 ? "down" : point.trend;

    return {
      ...point,
      predicted_income: round2(Math.max(0, blendedIncome)),
      predicted_variable: round2(predictedVariable),
      predicted_expenses: round2(predictedExpenses),
      trend,
    };
  });
}

function applyScenarioAdjustments(
  points: ForecastPoint[],
  growthAdjustment: number,
  costAdjustment: number,
  volatilityMultiplier: number,
  mode: "optimistic" | "pessimistic",
): ForecastPoint[] {
  return points.map((point, idx) => {
    const horizonFactor = 1 + ((idx + 1) * (volatilityMultiplier - 1) * 0.05);
    const signedGrowth = mode === "optimistic" ? Math.abs(growthAdjustment) : -Math.abs(growthAdjustment);
    const signedCost = mode === "optimistic" ? -Math.abs(costAdjustment) : Math.abs(costAdjustment);

    const adjustedIncome = Math.max(0, point.predicted_income * (1 + signedGrowth) * horizonFactor);
    const adjustedExpenses = Math.max(0, point.predicted_expenses * (1 + signedCost) * horizonFactor);
    const fixedRatio = safeDiv(point.predicted_fixed, point.predicted_expenses || 1);
    const adjustedFixed = Math.max(0, adjustedExpenses * clamp(fixedRatio, 0, 1));
    const adjustedVariable = Math.max(0, adjustedExpenses - adjustedFixed);

    const trend: "up" | "down" | "stable" =
      adjustedExpenses > point.predicted_expenses * 1.05
        ? "up"
        : adjustedExpenses < point.predicted_expenses * 0.95
          ? "down"
          : "stable";

    return {
      ...point,
      predicted_income: round2(adjustedIncome),
      predicted_expenses: round2(adjustedExpenses),
      predicted_fixed: round2(adjustedFixed),
      predicted_variable: round2(adjustedVariable),
      trend,
    };
  });
}

export function generateScenarios(
  baseForecast: ForecastPoint[],
  config: ForecastEngineConfig,
): ForecastScenarios {
  const optimistic = applyScenarioAdjustments(
    baseForecast,
    config.scenarios.optimistic.growthAdjustment,
    config.scenarios.optimistic.costAdjustment,
    config.scenarios.optimistic.volatilityMultiplier,
    "optimistic",
  );

  const pessimistic = applyScenarioAdjustments(
    baseForecast,
    config.scenarios.stress.growthAdjustment,
    config.scenarios.stress.costAdjustment,
    config.scenarios.stress.volatilityMultiplier,
    "pessimistic",
  );

  return {
    base: [...baseForecast],
    optimistic,
    pessimistic,
  };
}

function debtMap(schedule: DebtInstallment[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const item of schedule) {
    out[item.month] = (out[item.month] ?? 0) + Math.max(0, item.amount);
  }
  return out;
}

function applyConstraintsToSeries(
  scenarioName: "base" | "optimistic" | "pessimistic",
  series: ForecastPoint[],
  config: ForecastEngineConfig,
): ConstraintApplicationResult {
  const warnings: string[] = [];
  const constrained: ForecastPoint[] = [];
  const debtByMonth = debtMap(config.constraints.debtSchedule);
  const monthlyGrowthCap = Math.pow(1 + Math.max(0, config.constraints.maxAnnualGrowthCap), 1 / 12) - 1;
  let rollingCash = config.constraints.openingCash;
  let cappedGrowthHit = false;
  let capacityHit = false;
  let negativeCashAlert = false;
  let totalBurn = 0;

  for (let i = 0; i < series.length; i += 1) {
    const original = series[i];
    let income = original.predicted_income;
    let expenses = original.predicted_expenses;
    let fixed = original.predicted_fixed;
    let variable = original.predicted_variable;

    if (i > 0) {
      const prevIncome = constrained[i - 1].predicted_income;
      const maxIncome = prevIncome * (1 + monthlyGrowthCap);
      if (income > maxIncome) {
        income = maxIncome;
        if (!cappedGrowthHit) {
          warnings.push(`Scenario ${scenarioName}: crescita ricavi limitata dal cap economico.`);
          cappedGrowthHit = true;
        }
      }
    }

    const capacityCap = config.constraints.maxMonthlyCapacityRevenue;
    if (capacityCap !== null && capacityCap > 0 && income > capacityCap) {
      income = capacityCap;
      if (!capacityHit) {
        warnings.push(`Scenario ${scenarioName}: ricavi limitati dalla capacita operativa massima.`);
        capacityHit = true;
      }
    }

    const debtInstallment = debtByMonth[original.month] ?? 0;
    if (debtInstallment > 0) {
      expenses += debtInstallment;
      fixed += debtInstallment;
      variable = Math.max(0, expenses - fixed);
    }

    rollingCash += income - expenses;
    totalBurn += Math.max(0, expenses - income);
    if (rollingCash < 0 && !negativeCashAlert) {
      warnings.push(`Scenario ${scenarioName}: possibile cassa negativa dal mese ${original.label}.`);
      negativeCashAlert = true;
    }

    constrained.push({
      ...original,
      predicted_income: round2(Math.max(0, income)),
      predicted_expenses: round2(Math.max(0, expenses)),
      predicted_fixed: round2(Math.max(0, fixed)),
      predicted_variable: round2(Math.max(0, variable)),
    });
  }

  const averageMonthlyBurn = constrained.length > 0 ? totalBurn / constrained.length : 0;
  return {
    constrained,
    warnings,
    endingCash: round2(rollingCash),
    averageMonthlyBurn: round2(averageMonthlyBurn),
  };
}

export function applyEconomicConstraints(
  scenarios: ForecastScenarios,
  config: ForecastEngineConfig,
): {
  constrainedScenarios: ForecastScenarios;
  warnings: string[];
  endingCash: number;
  averageMonthlyBurn: number;
} {
  const base = applyConstraintsToSeries("base", scenarios.base, config);
  const optimistic = applyConstraintsToSeries("optimistic", scenarios.optimistic, config);
  const pessimistic = applyConstraintsToSeries("pessimistic", scenarios.pessimistic, config);

  return {
    constrainedScenarios: {
      base: base.constrained,
      optimistic: optimistic.constrained,
      pessimistic: pessimistic.constrained,
    },
    warnings: [...base.warnings, ...optimistic.warnings, ...pessimistic.warnings],
    endingCash: base.endingCash,
    averageMonthlyBurn: base.averageMonthlyBurn,
  };
}

function createSeededRng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function randomNormal(rng: () => number): number {
  // Deterministic Box-Muller transform
  const u1 = clamp(rng(), 1e-9, 1 - 1e-9);
  const u2 = clamp(rng(), 1e-9, 1 - 1e-9);
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = clamp((p / 100) * (sorted.length - 1), 0, sorted.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  const weight = index - lower;
  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
}

export function simulateRisk(
  baseScenario: ForecastPoint[],
  config: ForecastEngineConfig,
): RiskSimulationResult | null {
  if (!config.monteCarlo.enabled || baseScenario.length === 0) return null;

  const iterations = Math.max(50, config.monteCarlo.iterations);
  const rng = createSeededRng(config.monteCarlo.seed);
  const endingCashSamples: number[] = [];
  let crisisCount = 0;

  for (let i = 0; i < iterations; i += 1) {
    let cash = config.constraints.openingCash;
    let crisis = false;

    for (const month of baseScenario) {
      const growthShock = randomNormal(rng) * config.monteCarlo.growthStdDev;
      const costShock = randomNormal(rng) * config.monteCarlo.costStdDev;
      const collectionShock = randomNormal(rng) * config.monteCarlo.collectionStdDev;

      const income = Math.max(0, month.predicted_income * (1 + growthShock + collectionShock));
      const expenses = Math.max(0, month.predicted_expenses * (1 + costShock));

      cash += income - expenses;
      if (cash < 0) {
        crisis = true;
      }
    }

    if (crisis) crisisCount += 1;
    endingCashSamples.push(cash);
  }

  const p10 = percentile(endingCashSamples, 10);
  const p90 = percentile(endingCashSamples, 90);
  const expected = endingCashSamples.reduce((acc, cur) => acc + cur, 0) / endingCashSamples.length;

  return {
    iterations,
    liquidity_crisis_probability: round2((crisisCount / iterations) * 100),
    confidence_interval_80: {
      p10: round2(p10),
      p90: round2(p90),
    },
    ending_cash_expected: round2(expected),
  };
}

function scenarioSpread(scenarios: ForecastScenarios): number {
  if (scenarios.base.length === 0) return 0;
  let spreadAccumulator = 0;

  for (let i = 0; i < scenarios.base.length; i += 1) {
    const base = scenarios.base[i];
    const optimistic = scenarios.optimistic[i];
    const pessimistic = scenarios.pessimistic[i];
    const incomeSpread = Math.abs((optimistic?.predicted_income ?? 0) - (pessimistic?.predicted_income ?? 0));
    const expenseSpread = Math.abs((optimistic?.predicted_expenses ?? 0) - (pessimistic?.predicted_expenses ?? 0));
    const normalizer = Math.max(1, base.predicted_income + base.predicted_expenses);
    spreadAccumulator += ((incomeSpread + expenseSpread) / normalizer) * 100;
  }

  return spreadAccumulator / scenarios.base.length;
}

export function computeConfidence(params: {
  dataLength: number;
  volatility: number;
  driverPresence: boolean;
  scenarioSpread: number;
  monthsToPredict: number;
  useLegacyFallback: boolean;
}): {
  overall: number;
  perMonth: number[];
} {
  const lengthScore = clamp(params.dataLength / 24, 0, 1);
  const volatilityScore = 1 - clamp(params.volatility / 80, 0, 1);
  const driverScore = params.driverPresence ? 1 : 0.72;
  const spreadScore = 1 - clamp(params.scenarioSpread / 60, 0, 1);

  let overall =
    (0.35 * lengthScore + 0.25 * volatilityScore + 0.2 * driverScore + 0.2 * spreadScore) * 100;

  if (
    params.useLegacyFallback &&
    (!Number.isFinite(overall) || params.dataLength < 2)
  ) {
    overall = Math.max(40, 85 - Math.min(params.volatility * 0.5, 20));
  }

  overall = clamp(overall, 30, 95);

  const perMonth = new Array(params.monthsToPredict).fill(0).map((_, idx) => {
    if (params.useLegacyFallback && params.dataLength < 6) {
      const legacy = 85 - (idx + 1) * 5 - Math.min(params.volatility * 0.5, 20);
      return Math.round(clamp(legacy, 40, 90));
    }
    return Math.round(clamp(overall - (idx + 1) * 3, 30, 95));
  });

  return {
    overall: Math.round(overall),
    perMonth,
  };
}

function runwayMonths(openingCash: number, averageMonthlyBurn: number): number | null {
  if (openingCash <= 0 || averageMonthlyBurn <= 0) return null;
  return round2(openingCash / averageMonthlyBurn);
}

function mapToLegacyInsights(insights: ForecastInsight[]): LegacyInsight[] {
  return insights.map((insight) => {
    if (insight.type === "risk") {
      return {
        type: insight.severity >= 4 ? "warning" : "info",
        title: insight.severity >= 4 ? "Rischio elevato" : "Attenzione",
        message: insight.message,
      };
    }
    return {
      type: insight.severity >= 4 ? "success" : "info",
      title: insight.severity >= 4 ? "Opportunita rilevante" : "Opportunita",
      message: insight.message,
    };
  });
}

export function generateInsights(params: {
  historical: MonthlyData[];
  forecast: ForecastPoint[];
  statistics: {
    avgExpenses: number;
    avgIncome: number;
    avgFixed: number;
    expenseTrendPct: number;
    incomeTrendPct: number;
    volatility: number;
  };
  riskSimulation: RiskSimulationResult | null;
  warnings: string[];
  openingCash: number;
  averageMonthlyBurn: number;
}): ForecastInsight[] {
  const insights: ForecastInsight[] = [];

  if (params.statistics.incomeTrendPct < -3) {
    insights.push({
      type: "risk",
      severity: 4,
      message: `Trend ricavi negativo (${params.statistics.incomeTrendPct.toFixed(1)}%): valuta azioni commerciali correttive.`,
    });
  } else if (params.statistics.incomeTrendPct > 3) {
    insights.push({
      type: "opportunity",
      severity: 3,
      message: `Trend ricavi in crescita (${params.statistics.incomeTrendPct.toFixed(1)}%): possibile spazio per investimenti mirati.`,
    });
  }

  const fixedRatio = params.statistics.avgExpenses > 0
    ? (params.statistics.avgFixed / params.statistics.avgExpenses) * 100
    : 0;
  if (fixedRatio > 70) {
    insights.push({
      type: "risk",
      severity: fixedRatio > 80 ? 4 : 3,
      message: `Costi fissi elevati (${fixedRatio.toFixed(0)}% delle spese): riducono la flessibilita operativa.`,
    });
  } else if (fixedRatio < 45) {
    insights.push({
      type: "opportunity",
      severity: 2,
      message: `Struttura costi relativamente flessibile (${fixedRatio.toFixed(0)}% fissi).`,
    });
  }

  const runway = runwayMonths(params.openingCash, params.averageMonthlyBurn);
  if (runway !== null) {
    if (runway < 6) {
      insights.push({
        type: "risk",
        severity: 5,
        message: `Runway di cassa stimata molto breve (${runway.toFixed(1)} mesi).`,
      });
    } else if (runway < 12) {
      insights.push({
        type: "risk",
        severity: 3,
        message: `Runway di cassa stimata ${runway.toFixed(1)} mesi: monitorare la liquidita.`,
      });
    } else {
      insights.push({
        type: "opportunity",
        severity: 3,
        message: `Runway di cassa confortevole (${runway.toFixed(1)} mesi).`,
      });
    }
  }

  if (params.riskSimulation) {
    const probability = params.riskSimulation.liquidity_crisis_probability;
    if (probability >= 40) {
      insights.push({
        type: "risk",
        severity: 5,
        message: `Monte Carlo: probabilita crisi liquidita ${probability.toFixed(1)}%.`,
      });
    } else if (probability >= 20) {
      insights.push({
        type: "risk",
        severity: 3,
        message: `Monte Carlo: rischio liquidita moderato (${probability.toFixed(1)}%).`,
      });
    } else {
      insights.push({
        type: "opportunity",
        severity: 2,
        message: `Monte Carlo: rischio liquidita contenuto (${probability.toFixed(1)}%).`,
      });
    }
  }

  if (params.warnings.length > 0) {
    insights.push({
      type: "risk",
      severity: 3,
      message: `Vincoli economici applicati: ${params.warnings.length} segnalazioni di stabilita.`,
    });
  }

  const avgSavings = params.statistics.avgIncome - params.statistics.avgExpenses;
  if (avgSavings > 0) {
    insights.push({
      type: "opportunity",
      severity: avgSavings > params.statistics.avgIncome * 0.2 ? 4 : 3,
      message: `Risparmio medio positivo (${round2(avgSavings)} EUR/mese).`,
    });
  } else {
    insights.push({
      type: "risk",
      severity: 4,
      message: `Spese medie superiori alle entrate di ${round2(Math.abs(avgSavings))} EUR/mese.`,
    });
  }

  return insights;
}

export function runForecastEngine(input: ForecastEngineInput): ForecastEngineOutput {
  const config = getDefaultForecastEngineConfig(input.config);
  const historical = [...input.historical].sort((a, b) => a.month.localeCompare(b.month));

  if (historical.length < 2) {
    const warningMessage = "Dati insufficienti: servono almeno 2 mesi di storico.";
    const lowDataInsights: ForecastInsight[] = [
      {
        type: "risk",
        severity: 3,
        message: "Storico ridotto: il forecast usa fallback conservativo.",
      },
    ];
    return {
      historical,
      forecasts: [],
      forecast_scenarios: { base: [], optimistic: [], pessimistic: [] },
      advanced_insights: lowDataInsights,
      insights: mapToLegacyInsights(lowDataInsights).concat([
        {
          type: "warning",
          title: "Dati insufficienti",
          message: warningMessage,
        },
      ]),
      warnings: [warningMessage],
      statistics: {
        avg_monthly_expenses: 0,
        avg_monthly_income: 0,
        expense_trend: 0,
        income_trend: 0,
        volatility: 0,
        confidence_score: 40,
        runway_months: null,
      },
      risk_simulation: null,
    };
  }

  const statistical = computeStatisticalForecast(historical, input.monthsToPredict, config);
  const driverAdjusted = computeDriverForecast(statistical.points, historical, input.drivers, config);
  const rawScenarios = generateScenarios(driverAdjusted, config);
  const constrained = applyEconomicConstraints(rawScenarios, config);

  const driverPresence = Boolean(
    input.drivers &&
      (
        (input.drivers.active_clients_forecast?.length ?? 0) > 0 ||
        (input.drivers.volume_forecast?.length ?? 0) > 0
      ),
  );
  const spread = scenarioSpread(constrained.constrainedScenarios);
  const confidence = computeConfidence({
    dataLength: historical.length,
    volatility: statistical.volatility,
    driverPresence,
    scenarioSpread: spread,
    monthsToPredict: input.monthsToPredict,
    useLegacyFallback: config.useLegacyConfidenceFallback,
  });

  const baseWithConfidence = constrained.constrainedScenarios.base.map((point, idx) => ({
    ...point,
    confidence: confidence.perMonth[idx] ?? confidence.overall,
  }));
  const optimisticWithConfidence = constrained.constrainedScenarios.optimistic.map((point, idx) => ({
    ...point,
    confidence: confidence.perMonth[idx] ?? confidence.overall,
  }));
  const pessimisticWithConfidence = constrained.constrainedScenarios.pessimistic.map((point, idx) => ({
    ...point,
    confidence: confidence.perMonth[idx] ?? confidence.overall,
  }));

  const riskSimulation = simulateRisk(baseWithConfidence, config);
  const advancedInsights = generateInsights({
    historical,
    forecast: baseWithConfidence,
    statistics: {
      avgExpenses: statistical.avgExpenses,
      avgIncome: statistical.avgIncome,
      avgFixed: statistical.avgFixed,
      expenseTrendPct: statistical.expenseTrendPct,
      incomeTrendPct: statistical.incomeTrendPct,
      volatility: statistical.volatility,
    },
    riskSimulation,
    warnings: constrained.warnings,
    openingCash: config.constraints.openingCash,
    averageMonthlyBurn: constrained.averageMonthlyBurn,
  });

  const legacyInsights = mapToLegacyInsights(advancedInsights);
  if (constrained.warnings.length > 0) {
    for (const warning of constrained.warnings) {
      legacyInsights.push({
        type: "warning",
        title: "Vincolo economico applicato",
        message: warning,
      });
    }
  }

  const statistics: ForecastStatistics = {
    avg_monthly_expenses: round2(statistical.avgExpenses),
    avg_monthly_income: round2(statistical.avgIncome),
    expense_trend: round2(statistical.expenseTrendPct),
    income_trend: round2(statistical.incomeTrendPct),
    volatility: round2(statistical.volatility),
    confidence_score: confidence.overall,
    runway_months: runwayMonths(config.constraints.openingCash, constrained.averageMonthlyBurn),
  };

  return {
    historical,
    forecasts: baseWithConfidence,
    forecast_scenarios: {
      base: baseWithConfidence,
      optimistic: optimisticWithConfidence,
      pessimistic: pessimisticWithConfidence,
    },
    advanced_insights: advancedInsights,
    insights: legacyInsights,
    warnings: constrained.warnings,
    statistics,
    risk_simulation: riskSimulation,
  };
}
